import { calculateKundali } from './vedicEngine';
import { BirthDetails, CompleteKundali } from '../types/kundali';

export interface GoldenTestCase {
  id: string;
  name: string;
  description: string;
  birthDetails: BirthDetails;
  expectedLagnaSign: string;
  expectedMoonSign: string;
  expectedSunSign: string;
  expectedSAVTotal: number; // Strictly 337 in classical Parashari system
  expectedD1HouseCount: number;
  expectedPlanetCount: number;
}

export interface RegressionTestResult {
  testId: string;
  testName: string;
  passed: boolean;
  lagnaMatch: boolean;
  actualLagna: string;
  moonMatch: boolean;
  actualMoon: string;
  sunMatch: boolean;
  actualSun: string;
  savTotalMatch: boolean;
  actualSAVTotal: number;
  divisionalChartsVerified: boolean;
  dashaProgressionValid: boolean;
  executionTimeMs: number;
  errorMessages: string[];
}

export interface GoldenRegressionSummary {
  timestamp: string;
  engineVersion: string;
  totalTests: number;
  passedTests: number;
  failedTests: number;
  allPassed: boolean;
  totalExecutionTimeMs: number;
  results: RegressionTestResult[];
}

// 3 Gold-Standard Reference Charts for Vedic Jyotish Calibration
export const GOLDEN_REFERENCE_CASES: GoldenTestCase[] = [
  {
    id: 'GOLDEN_REF_1_NEW_DELHI_1990',
    name: 'Standard Reference Chart 1 (New Delhi 1990)',
    description: 'Reference Chart for Lagna, Solar, and Lunar ephemeris calibration.',
    birthDetails: {
      name: 'Golden Reference Native A',
      gender: 'male',
      date: '1990-01-15',
      time: '14:30:00',
      latitude: 28.6139,
      longitude: 77.2090,
      placeName: 'New Delhi, India',
      timezoneOffsetMinutes: 330
    },
    expectedLagnaSign: 'Taurus',
    expectedMoonSign: 'Leo',
    expectedSunSign: 'Capricorn',
    expectedSAVTotal: 337,
    expectedD1HouseCount: 12,
    expectedPlanetCount: 10 // Ascendant + 9 grahas
  },
  {
    id: 'GOLDEN_REF_2_MUMBAI_1985',
    name: 'Standard Reference Chart 2 (Mumbai 1985)',
    description: 'Reference Chart for Ashtakavarga 337 bindus and Vimshottari dasha balance.',
    birthDetails: {
      name: 'Golden Reference Native B',
      gender: 'female',
      date: '1985-07-20',
      time: '08:15:00',
      latitude: 18.9220,
      longitude: 72.8347,
      placeName: 'Mumbai, India',
      timezoneOffsetMinutes: 330
    },
    expectedLagnaSign: 'Leo',
    expectedMoonSign: 'Leo',
    expectedSunSign: 'Cancer',
    expectedSAVTotal: 337,
    expectedD1HouseCount: 12,
    expectedPlanetCount: 10
  },
  {
    id: 'GOLDEN_REF_3_BANGALORE_2000',
    name: 'Standard Reference Chart 3 (Bangalore 2000 - Millennium Chart)',
    description: 'Reference Chart for high-precision planetary speed, retrograde states, and vargas.',
    birthDetails: {
      name: 'Golden Reference Native C',
      gender: 'male',
      date: '2000-01-01',
      time: '00:01:00',
      latitude: 12.9716,
      longitude: 77.5946,
      placeName: 'Bangalore, India',
      timezoneOffsetMinutes: 330
    },
    expectedLagnaSign: 'Virgo',
    expectedMoonSign: 'Libra',
    expectedSunSign: 'Sagittarius',
    expectedSAVTotal: 337,
    expectedD1HouseCount: 12,
    expectedPlanetCount: 10
  }
];

/**
 * Runs the Golden Kundali Regression Suite and verifies deterministic outputs
 */
export function runGoldenKundaliRegression(): GoldenRegressionSummary {
  const startTime = performance.now();
  const results: RegressionTestResult[] = [];

  for (const testCase of GOLDEN_REFERENCE_CASES) {
    const t0 = performance.now();
    const errors: string[] = [];
    let computed: CompleteKundali | null = null;

    try {
      computed = calculateKundali(testCase.birthDetails);
    } catch (err: any) {
      errors.push(`Calculation crash: ${err?.message || String(err)}`);
    }

    if (!computed) {
      results.push({
        testId: testCase.id,
        testName: testCase.name,
        passed: false,
        lagnaMatch: false,
        actualLagna: 'ERROR',
        moonMatch: false,
        actualMoon: 'ERROR',
        sunMatch: false,
        actualSun: 'ERROR',
        savTotalMatch: false,
        actualSAVTotal: 0,
        divisionalChartsVerified: false,
        dashaProgressionValid: false,
        executionTimeMs: Math.round(performance.now() - t0),
        errorMessages: errors
      });
      continue;
    }

    // 1. Verify Lagna (Ascendant)
    const ascPlanet = computed.planets.find((p) => p.name === 'Ascendant');
    const actualLagna = ascPlanet ? ascPlanet.sign : computed.houses[0]?.sign || 'Unknown';
    const lagnaMatch = actualLagna === testCase.expectedLagnaSign;
    if (!lagnaMatch) {
      errors.push(`Lagna sign mismatch: expected ${testCase.expectedLagnaSign}, got ${actualLagna}`);
    }

    // 2. Verify Moon Sign
    const moonPlanet = computed.planets.find((p) => p.name === 'Moon');
    const actualMoon = moonPlanet ? moonPlanet.sign : 'Unknown';
    const moonMatch = actualMoon === testCase.expectedMoonSign;
    if (!moonMatch) {
      errors.push(`Moon sign mismatch: expected ${testCase.expectedMoonSign}, got ${actualMoon}`);
    }

    // 3. Verify Sun Sign
    const sunPlanet = computed.planets.find((p) => p.name === 'Sun');
    const actualSun = sunPlanet ? sunPlanet.sign : 'Unknown';
    const sunMatch = actualSun === testCase.expectedSunSign;
    if (!sunMatch) {
      errors.push(`Sun sign mismatch: expected ${testCase.expectedSunSign}, got ${actualSun}`);
    }

    // 4. Verify Sarvashtakvarga Total (Must be 337 in Parashari system)
    let actualSAVTotal = 0;
    if (computed.ashtakvarga?.sarvashtakvarga) {
      actualSAVTotal = Object.values(computed.ashtakvarga.sarvashtakvarga).reduce((a, b) => a + b, 0);
    }
    const savTotalMatch = actualSAVTotal === testCase.expectedSAVTotal;
    if (!savTotalMatch) {
      errors.push(`SAV total bindus mismatch: expected ${testCase.expectedSAVTotal}, got ${actualSAVTotal}`);
    }

    // 5. Verify Divisional Charts presence
    const hasD1 = !!computed.charts?.D1;
    const hasD9 = !!computed.charts?.D9;
    const hasD10 = !!computed.charts?.D10;
    const divisionalChartsVerified = hasD1 && hasD9 && hasD10;
    if (!divisionalChartsVerified) {
      errors.push('Missing key divisional charts (D1, D9, or D10)');
    }

    // 6. Verify Vimshottari Dasha Progression
    const dashaProgressionValid = Array.isArray(computed.dashas) && computed.dashas.length >= 9;
    if (!dashaProgressionValid) {
      errors.push('Vimshottari Dasha sequence length less than 9 planetary periods');
    }

    const passed = errors.length === 0;

    results.push({
      testId: testCase.id,
      testName: testCase.name,
      passed,
      lagnaMatch,
      actualLagna,
      moonMatch,
      actualMoon,
      sunMatch,
      actualSun,
      savTotalMatch,
      actualSAVTotal,
      divisionalChartsVerified,
      dashaProgressionValid,
      executionTimeMs: Math.round(performance.now() - t0),
      errorMessages: errors
    });
  }

  const totalExecutionTimeMs = Math.round(performance.now() - startTime);
  const passedTests = results.filter((r) => r.passed).length;
  const failedTests = results.length - passedTests;

  return {
    timestamp: new Date().toISOString(),
    engineVersion: 'VedangaEngine-1.2.0-Astrodienst-Compatible',
    totalTests: results.length,
    passedTests,
    failedTests,
    allPassed: failedTests === 0,
    totalExecutionTimeMs,
    results
  };
}
